import { uniq } from 'lodash';
import moment, { Moment } from 'moment';
import {
  Allocation,
  AllocationSourceType,
} from 'api/resource-management/types';
import { WorkingDaysConfiguration } from 'components/organisms/user-capacity/types';
import { defaultFormat } from 'utils/date-field/formatter';
import { floatUid } from 'utils/uid';
import { isNonWorkingDay } from 'utils/working-days';
import { AllocationEvent } from '../event-types';
import {
  getMaximumEndDate,
  getMinimumStartDate,
} from './resources-summary/common';

const getEventsInADay = <T extends Allocation | AllocationEvent>(
  day: Moment,
  allocations: T[],
): T[] =>
  allocations.filter(
    ({ startDate: collidingStartDate, endDate: collidingEndDate }) => {
      return day.isBetween(
        moment(collidingStartDate),
        moment(collidingEndDate),
        'day',
        '[]',
      );
    },
  );
const getTotalMinutesFromAllocations = <T extends Allocation | AllocationEvent>(
  allocations: T[],
): number => allocations.reduce((total, event) => total + event.minutes, 0);

const getSourceIds = (allocations: AllocationEvent[]) =>
  allocations.reduce(
    (acc, allocation) =>
      acc.concat((allocation.sources ?? []).map(({ sourceId }) => sourceId)),
    [] as number[],
  );

const getSourceType = (allocations: AllocationEvent[]) =>
  allocations?.[0]?.sources?.[0]?.sourceType ?? AllocationSourceType.Task;

const getIncludeWeekendFromAllocations = (
  allocationEvents: AllocationEvent[],
) =>
  allocationEvents.reduce(
    (acc, e) => ({
      includeSaturday: acc.includeSaturday || !!e.includeSaturday,
      includeSunday: acc.includeSunday || !!e.includeSunday,
    }),
    { includeSaturday: false, includeSunday: false },
  );

export const mergeAllocations = <T extends AllocationEvent>(
  allocations: T[],
  workingDaysConfig: WorkingDaysConfiguration,
): T[] => {
  const minimumStartDateFromAllocation = getMinimumStartDate(allocations);
  const maximumEndDateFromAllocation = getMaximumEndDate(allocations);
  let currentDay = moment(minimumStartDateFromAllocation);
  const mergedAllocation: T[] = [];

  let previousAllocationParams: {
    startDate: Moment;
    minutes: number;
    referenceAllocation: T;
    sourceType?: AllocationSourceType;
    sourceIds?: number[];
    includeSaturday: boolean;
    includeSunday: boolean;
  } | null = null;

  while (currentDay.isSameOrBefore(maximumEndDateFromAllocation)) {
    if (!isNonWorkingDay(currentDay, workingDaysConfig)) {
      const eventsInADay = getEventsInADay(currentDay, allocations);
      const totalMinutesForDay = getTotalMinutesFromAllocations(eventsInADay);

      if (previousAllocationParams == null && eventsInADay.length > 0) {
        const weekend = getIncludeWeekendFromAllocations(eventsInADay);

        previousAllocationParams = {
          startDate: currentDay,
          minutes: totalMinutesForDay,
          referenceAllocation: eventsInADay[0],
          sourceType: getSourceType(eventsInADay),
          sourceIds: getSourceIds(eventsInADay),
          includeSaturday: weekend.includeSaturday,
          includeSunday: weekend.includeSunday,
        };
      } else if (
        previousAllocationParams != null &&
        totalMinutesForDay !== previousAllocationParams.minutes
      ) {
        const { sourceType } = previousAllocationParams;

        mergedAllocation.push({
          ...previousAllocationParams.referenceAllocation,
          id: floatUid(),
          startDate: defaultFormat(
            // COMMENTED OUT: Shifting non-working days logic
            // shiftDateIfOnNonWorkingDay(
            previousAllocationParams.startDate,
            //   workingDaysConfig,
            // ),
          ),
          endDate: defaultFormat(
            // shiftDateIfOnNonWorkingDay(
            currentDay.subtract(1, 'day').endOf('day'),
            //   workingDaysConfig,
            //   false,
            // ),
          ),
          minutes: previousAllocationParams.minutes,
          includeSaturday: previousAllocationParams.includeSaturday,
          includeSunday: previousAllocationParams.includeSunday,
          sources:
            previousAllocationParams.sourceIds != null
              ? uniq(previousAllocationParams.sourceIds).map((x) => ({
                  sourceType: sourceType ?? AllocationSourceType.Task,
                  sourceId: x,
                }))
              : undefined,
        });
        if (eventsInADay.length > 0) {
          const weekend = getIncludeWeekendFromAllocations(eventsInADay);

          previousAllocationParams = {
            startDate: currentDay,
            minutes: totalMinutesForDay,
            referenceAllocation: eventsInADay[0],
            sourceType: getSourceType(eventsInADay),
            sourceIds: getSourceIds(eventsInADay),
            includeSaturday: weekend.includeSaturday,
            includeSunday: weekend.includeSunday,
          };
        } else {
          previousAllocationParams = null;
        }
      } else if (
        eventsInADay.length > 0 &&
        previousAllocationParams?.sourceIds != null
      ) {
        previousAllocationParams.sourceIds.push(...getSourceIds(eventsInADay));
        const weekend = getIncludeWeekendFromAllocations(eventsInADay);

        previousAllocationParams.includeSaturday =
          previousAllocationParams.includeSaturday || weekend.includeSaturday;
        previousAllocationParams.includeSunday =
          previousAllocationParams.includeSunday || weekend.includeSunday;
      }
    }
    currentDay = currentDay.add(1, 'day');
  }
  if (previousAllocationParams != null) {
    const { sourceType } = previousAllocationParams;

    mergedAllocation.push({
      ...previousAllocationParams.referenceAllocation,
      id: floatUid(),
      startDate: defaultFormat(
        // COMMENTED OUT: Shifting non-working days logic
        // shiftDateIfOnNonWorkingDay(
        previousAllocationParams.startDate,
        //   workingDaysConfig,
        // ),
      ),
      endDate: defaultFormat(
        // shiftDateIfOnNonWorkingDay(
        maximumEndDateFromAllocation.endOf('day'),
        //   workingDaysConfig,
        //   false,
        // ),
      ),
      minutes: previousAllocationParams.minutes,
      includeSaturday: previousAllocationParams.includeSaturday,
      includeSunday: previousAllocationParams.includeSunday,
      sources:
        previousAllocationParams.sourceIds != null
          ? uniq(previousAllocationParams.sourceIds).map((x) => ({
              sourceType: sourceType ?? AllocationSourceType.Task,
              sourceId: x,
            }))
          : undefined,
    });
  }

  return mergedAllocation;
};
